import axios, { type AxiosError } from "axios";

/** Standard backend error payload returned by API endpoints. */
export interface BackendErrorPayload {
  errorCode: number;
  errorMessage: string;
  errorType: string;
  metadata: Record<string, string | number | boolean | null>;
  referenceId: string;
}

interface BackendValidationErrorsPayload {
  readonly errors: Readonly<Record<string, readonly string[]>>;
}

export interface GetBackendValidationErrorMessageParams {
  readonly field: string;
  readonly payload: unknown;
}

/** Axios error typed with the backend error payload. */
export type BackendAxiosError = AxiosError<BackendErrorPayload>;

const isUnknownRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

const isStringArray = (value: unknown): value is string[] => {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
};

const isBackendErrorPayload = (
  value: unknown,
): value is BackendErrorPayload => {
  if (!isUnknownRecord(value)) return false;

  return (
    typeof value.errorCode === "number" &&
    typeof value.errorMessage === "string" &&
    typeof value.errorType === "string" &&
    typeof value.referenceId === "string"
  );
};

const isBackendValidationErrors = (
  value: unknown,
): value is Record<string, string[]> => {
  return isUnknownRecord(value) && Object.values(value).every(isStringArray);
};

const isBackendValidationErrorPayload = (
  value: unknown,
): value is BackendValidationErrorsPayload => {
  if (!isUnknownRecord(value)) return false;
  return isBackendValidationErrors(value.errors);
};

/** Returns the backend error payload from an Axios error, if present. */
export const getBackendErrorPayload = (
  error: BackendAxiosError | null | undefined,
): BackendErrorPayload | null => {
  const payload = error?.response?.data;
  return isBackendErrorPayload(payload) ? payload : null;
};

/** Returns the backend error message from an Axios error, if present. */
export const getBackendErrorMessage = (
  error: BackendAxiosError | null | undefined,
): string | null => {
  return getBackendErrorPayload(error)?.errorMessage ?? null;
};

export const getBackendValidationErrorMessage = ({
  field,
  payload,
}: GetBackendValidationErrorMessageParams): string | null => {
  if (!isBackendValidationErrorPayload(payload)) return null;
  return payload.errors[field]?.[0] ?? null;
};

/** Returns the backend error payload from an unknown error, if present. */
export const getBackendErrorPayloadFromUnknown = (
  error: unknown,
): BackendErrorPayload | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const payload = error.response?.data;
  return isBackendErrorPayload(payload) ? payload : null;
};

/** Returns the backend error message from an unknown error, if present. */
export const getBackendErrorMessageFromUnknown = (
  error: unknown,
): string | null => {
  return getBackendErrorPayloadFromUnknown(error)?.errorMessage ?? null;
};
